import type { Edge } from '@cognite/fdm-client';

import type { EdgesByType, EdgeWithModelName } from '../types';

/**
 * Type guards for each edge type - these enable TypeScript's type narrowing
 */
const isReferenceChecklistItemsEdge = (
  edge: Edge
): edge is EdgeWithModelName<'referenceChecklistItems'> =>
  edge.modelName === 'referenceChecklistItems';

const isReferenceTemplateItemsEdge = (
  edge: Edge
): edge is EdgeWithModelName<'referenceTemplateItems'> =>
  edge.modelName === 'referenceTemplateItems';

const isReferenceMeasurementsEdge = (
  edge: Edge
): edge is EdgeWithModelName<'referenceMeasurements'> =>
  edge.modelName === 'referenceMeasurements';

const isReferenceSchedulesEdge = (
  edge: Edge
): edge is EdgeWithModelName<'referenceSchedules'> =>
  edge.modelName === 'referenceSchedules';

const isReferenceObservationsEdge = (
  edge: Edge
): edge is EdgeWithModelName<'referenceObservations'> =>
  edge.modelName === 'referenceObservations';

/**
 * Groups edges by their modelName for different handling strategies.
 *
 * Returns a record where:
 * - Keys are modelName values (edge types) present in the input
 * - Values are arrays of edges with that modelName
 *
 * This allows callers to process edges differently based on their type.
 * For example, some edge types may be converted to direct relations while others
 * are upserted as traditional edges.
 */
export const separateEdgesByType = (edges: Edge[]): EdgesByType => {
  const edgesByType: EdgesByType = {
    referenceChecklistItems: [],
    referenceTemplateItems: [],
    referenceMeasurements: [],
    referenceSchedules: [],
    referenceObservations: [],
  };

  for (const edge of edges) {
    if (isReferenceChecklistItemsEdge(edge)) {
      edgesByType.referenceChecklistItems.push(edge);
    } else if (isReferenceTemplateItemsEdge(edge)) {
      edgesByType.referenceTemplateItems.push(edge);
    } else if (isReferenceMeasurementsEdge(edge)) {
      edgesByType.referenceMeasurements.push(edge);
    } else if (isReferenceSchedulesEdge(edge)) {
      edgesByType.referenceSchedules.push(edge);
    } else if (isReferenceObservationsEdge(edge)) {
      edgesByType.referenceObservations.push(edge);
    } else {
      throw new Error(
        `Unknown edge type: ${edge.modelName}. Expected one of: referenceChecklistItems, referenceTemplateItems, referenceMeasurements, referenceSchedules, referenceObservations`
      );
    }
  }

  return edgesByType;
};
